import { useEffect, useRef } from 'react';

const useFocusTrap = (isOpen , onClose) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const focusableElementsRef = useRef([]);

  useEffect(() => {
    if (!isOpen) return;

    const modalNode = modalRef.current;
    if (!modalNode) return;

    previousFocusRef.current = document.activeElement;

    const getFocusableElements = () => {
      return Array.from(
        modalNode.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);
    };

    const updateFocusableElements = () => {
      focusableElementsRef.current = getFocusableElements();
    };

    updateFocusableElements();
    if (focusableElementsRef.current.length > 0) {
      focusableElementsRef.current[0].focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (onClose) onClose();
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        const tag = e.target?.tagName;
        const isField = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
        if (!isField) {
          e.stopPropagation();
          return;
        }
      }
      if (e.key !== 'Tab') return;

      updateFocusableElements();
      const list = focusableElementsRef.current;
      if (list.length === 0) return;

      const firstElement = list[0];
      const lastElement = list[list.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    modalNode.addEventListener('keydown', handleKeyDown, true);

    const observer = new MutationObserver(() => {
      updateFocusableElements();
    });
    observer.observe(modalNode, {
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'style', 'class'],
      subtree: true,
    });
    return () => {
      modalNode.removeEventListener('keydown', handleKeyDown, true);
      observer.disconnect();

      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return modalRef;
};

export default useFocusTrap;
